import { AnalyticsEventNameType, SmartAlertRunItem } from "@coeff/api";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

import {
  FlexColumn,
  FlexRow,
  Loader,
  Modal as BaseModal,
  Typography,
} from "../../../../components";
import { COLORS } from "../../../../constants";
import {
  getSmartAlertSuccessfulDeliveryCount,
  useSentryMountUnmountTransaction,
  useTrack,
} from "../../../../utils";

const Modal = styled(BaseModal)`
  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 8px;
  }
  .ant-modal-body {
    padding-top: 0;
  }
`;

const ModalContent = styled.div`
  display: flex;
  height: 400px;
`;

const LeftSidebar = styled.div`
  width: 100px;
  padding: 8px 0;
  overflow-y: auto;
  flex-shrink: 0;
`;

const TabItem = styled.div<{ isActive: boolean }>`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ isActive }) => (isActive ? COLORS.coeblue4 : COLORS.black65)};
  font-weight: ${({ isActive }) => (isActive ? 600 : 400)};

  &:hover {
    color: ${COLORS.coeblue4};
  }
`;

const CenterSection = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: ${COLORS.white};
  overflow-y: auto;
`;

const GeneratedImage = styled.img`
  width: 100%;
  max-width: 602px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NoImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.black45};
  font-size: 14px;
`;

const ImageLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ImageErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${COLORS.black45};
  font-size: 14px;
  text-align: center;
`;

const RightSection = styled.div`
  width: 320px;
  padding: 16px;
  flex-shrink: 0;
`;

const ReasoningSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${COLORS.black4};
  border-radius: 8px;
  padding: 16px;
  max-height: 100%;
  overflow-y: auto;
`;

const SectionTitle = styled(Typography)`
  font-weight: 700;
  font-size: 14px;
  color: #111215;
`;

const Headline = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #111215;
  line-height: 1.4;

  p {
    margin: 0;
  }
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #111215;
  font-size: 14px;
  line-height: 1.6;
`;

const BulletItem = styled.li`
  margin-bottom: 8px;
`;

const MarkdownContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #111215;

  p {
    margin: 0 0 12px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
    color: #111215;
  }
`;

const ContextTitle = styled(Typography)`
  font-weight: 700;
  font-size: 14px;
  color: #111215;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TriggerText = styled(Typography)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;

  &:hover {
    white-space: normal;
    overflow: visible;
  }
`;

type FanoutItem = {
  content_headline?: string;
  content_bullet_points?: string[];
  content_reasoning?: string;
  generated_image_url?: string;
};

type SmartAlertRunDetailModalProps = {
  run: SmartAlertRunItem;
  alertName?: string;
  smartAlertId?: string;
  onClose: () => void;
};

export const SmartAlertRunDetailModal: React.FC<SmartAlertRunDetailModalProps> = ({
  run,
  alertName,
  smartAlertId,
  onClose,
}) => {
  useSentryMountUnmountTransaction({ name: "SmartAlertRunDetailModal", op: "ui.modal" });
  const track = useTrack();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Build fanout items from the run data
  // If fanout_results exists, use those; otherwise, create a single item from run-level fields
  const fanoutItems: FanoutItem[] = useMemo(() => {
    if (run.fanout_results && run.fanout_results.length > 0) {
      // Fanout results use shortened field names: headline, bullet_points, reasoning, image_url
      return run.fanout_results.map(result => ({
        content_headline: result.headline,
        content_bullet_points: result.bullet_points,
        content_reasoning: result.reasoning,
        generated_image_url: result.image_url,
      }));
    }

    // Fallback to run-level fields for single alert
    return [
      {
        content_headline: run.content_headline,
        content_bullet_points: run.content_bullet_points,
        content_reasoning: run.content_reasoning,
        generated_image_url: run.generated_image_url,
      },
    ];
  }, [run]);

  const currentItem = fanoutItems[selectedIndex] ?? fanoutItems[0];
  const totalAlerts = getSmartAlertSuccessfulDeliveryCount(run);
  const hasFanout = fanoutItems.length > 1;

  // Reset image loading/error states when switching between fanout items
  useEffect(() => {
    if (currentItem.generated_image_url) {
      setImageLoading(true);
      setImageError(false);
    } else {
      setImageLoading(false);
      setImageError(false);
    }
  }, [selectedIndex, currentItem.generated_image_url]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const sentTimestamp = run.completed_at ?? run.started_at;
  const formattedTime = sentTimestamp ? moment(sentTimestamp).format("MMM DD, YYYY h:mm A") : "";

  const subjectLine = alertName ?? "Smart Alert";

  return (
    <Modal
      open={true}
      onCancel={onClose}
      width={1130}
      title={
        <ModalHeader>
          <Typography fontWeight={700} fontSize="16px">
            {totalAlerts} Alert{totalAlerts !== 1 ? "s" : ""}
          </Typography>
          <FlexRow gap={2}>
            <Typography fontSize="14px" color={COLORS.black50}>
              {subjectLine}
            </Typography>
            {formattedTime && (
              <Typography fontSize="14px" color={COLORS.black50}>
                {formattedTime}
              </Typography>
            )}
          </FlexRow>
          {run.trigger_reasoning && (
            <TriggerText fontSize="14px" color={COLORS.black50} title={run.trigger_reasoning}>
              Trigger:{" "}
              {run.trigger_reasoning.length > 100
                ? `${run.trigger_reasoning.substring(0, 100)}...`
                : run.trigger_reasoning}
            </TriggerText>
          )}
        </ModalHeader>
      }
    >
      <ModalContent>
        {/* Left sidebar - Fanout navigation tabs */}
        {hasFanout && (
          <LeftSidebar>
            {fanoutItems.map((_, index) => (
              <TabItem
                key={index}
                isActive={selectedIndex === index}
                onClick={() => {
                  track(AnalyticsEventNameType.SmartAlertRunFanoutTabSwitched, {
                    smart_alert_id: smartAlertId,
                    smart_alert_run_id: run.smart_alert_run_id,
                    fanout_index: index,
                    fanout_total: fanoutItems.length,
                  });
                  setSelectedIndex(index);
                }}
              >
                Alert {index + 1}
              </TabItem>
            ))}
          </LeftSidebar>
        )}

        {/* Center - Generated image display */}
        <CenterSection>
          {currentItem.generated_image_url ? (
            <>
              {imageLoading && (
                <ImageLoadingContainer>
                  <Loader size="large" />
                  <Typography color={COLORS.black45} fontSize="14px">
                    Loading image...
                  </Typography>
                </ImageLoadingContainer>
              )}
              {imageError ? (
                <ImageErrorContainer>
                  <Typography color={COLORS.black65}>Failed to load image</Typography>
                  <Typography color={COLORS.black45} fontSize="13px">
                    The image could not be displayed
                  </Typography>
                </ImageErrorContainer>
              ) : (
                <GeneratedImage
                  src={currentItem.generated_image_url}
                  alt="Generated alert image"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{ display: imageLoading ? "none" : "block" }}
                />
              )}
            </>
          ) : (
            <NoImagePlaceholder>No image generated for this alert</NoImagePlaceholder>
          )}
        </CenterSection>

        {/* Right section - Reasoning */}
        <RightSection>
          <ReasoningSection>
            <SectionTitle>Reasoning</SectionTitle>

            {/* Headline */}
            {currentItem.content_headline && (
              <Headline>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {currentItem.content_headline}
                </ReactMarkdown>
              </Headline>
            )}

            {/* Bullet points */}
            {currentItem.content_bullet_points && currentItem.content_bullet_points.length > 0 && (
              <BulletList>
                {currentItem.content_bullet_points.map((point, index) => (
                  <BulletItem key={index}>
                    <MarkdownContent>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{point}</ReactMarkdown>
                    </MarkdownContent>
                  </BulletItem>
                ))}
              </BulletList>
            )}

            {/* Context paragraph (content_reasoning) */}
            {currentItem.content_reasoning && (
              <FlexColumn gap={2}>
                <ContextTitle>Context</ContextTitle>
                <MarkdownContent>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {currentItem.content_reasoning}
                  </ReactMarkdown>
                </MarkdownContent>
              </FlexColumn>
            )}
          </ReasoningSection>
        </RightSection>
      </ModalContent>
    </Modal>
  );
};
